import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { PlatformLocation } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { combineLatest, filter, map, take } from 'rxjs';
import { AuthService, UserProfile } from '../../core/services/auth.service';
import { AccessControlService } from '../../core/services/access-control.service';
import { ModeService } from '../../core/services/mode.service';
import { TllLoginRedirectService } from '../../core/services/tll-login-redirect.service';

export function normalizePath(raw: string): string {
  const clean = raw.split('?')[0]?.split('#')[0] ?? '/';
  return clean === '' ? '/' : clean;
}

@Component({
  selector: 'app-root-redirect',
  standalone: true,
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RootRedirectComponent {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly ac = inject(AccessControlService);
  private readonly mode = inject(ModeService);
  private readonly platformLocation = inject(PlatformLocation);
  private readonly tllLoginRedirect = inject(TllLoginRedirectService);

  constructor() {
    // 1) Read the real browser path (more reliable than router.url at ctor time)
    const browserPath = this.platformLocation.pathname || normalizePath(this.router.url);
    const atRoot = browserPath === '/' || browserPath === '';

    // 2) If not at root, do nothing (preserve deep link/refresh)
    if (!atRoot) return;

    // 3) If there is an explicit returnUrl, do nothing — guards will handle it
    try {
      const tree = this.router.parseUrl(this.router.url);
      if (tree.queryParams?.['returnUrl']) return;
    } catch {
      // ignore
    }

    // 4) Wait for first NavigationEnd to confirm we are still at root
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        take(1),
      )
      .subscribe((e) => {
        const pathOnly = normalizePath(e.urlAfterRedirects);
        const stillRoot = pathOnly === '/' || pathOnly === '';
        if (!stillRoot) return;

        // 5) Now wait for auth readiness and decide once
        combineLatest([this.auth.authReady$, this.auth.isAuthenticated$, this.auth.userProfile$])
          .pipe(
            filter(([ready]) => !!ready),
            take(1),
            map(([_, isAuth, profile]) => ({ isAuth, profile })),
          )
          .subscribe(({ isAuth, profile }) => {
            if (!isAuth && this.tllLoginRedirect.shouldRedirectGuestToLogin()) {
              this.tllLoginRedirect.redirectToLogin();
              return;
            }
            const dest = isAuth ? this.resolveHome(profile) : '/course-catalog';
            const currentPath = normalizePath(this.router.url);
            if (dest && dest !== currentPath) {
              this.router.navigateByUrl(this.router.createUrlTree([dest], { queryParamsHandling: 'preserve' }), {
                replaceUrl: true,
              });
            }
          });
      });
  }

  private roleOf(profile: UserProfile | null): string {
    return (profile?.role ?? '').toString().trim().toLowerCase();
  }

  private isSuperAdmin(profile: UserProfile | null): boolean {
    return this.roleOf(profile) === 'superadmin';
  }

  private isSupportAdmin(profile: UserProfile | null): boolean {
    return this.roleOf(profile) === 'supportadmin';
  }

  private hasAdminAccess(profile: UserProfile | null): boolean {
    const role = this.roleOf(profile);
    return role === 'superadmin' || role === 'supportadmin' || role === 'groupadmin' || role === 'subgroupadmin';
  }

  private resolveHome(profile: UserProfile | null): string {
    // Hard rule: SuperAdmin & SupportAdmin home is ALWAYS admin area (no dashboard).
    if (this.isSuperAdmin(profile) || this.isSupportAdmin(profile)) {
      const fromAccessControl = this.ac.homeFor?.(profile, '/user-management');
      return fromAccessControl || '/user-management';
    }

    // Everyone else: prefer UI mode but never force admin if no admin access
    const preferredByMode = this.mode.isLearner() ? '/dashboard' : '/user-management';

    if (!this.mode.isLearner() && !this.hasAdminAccess(profile)) {
      return '/dashboard';
    }

    const fromAccessControl = this.ac.homeFor?.(profile, preferredByMode);
    return fromAccessControl || preferredByMode;
  }
}
